
import React, { useState } from 'react';
import { 
  Table, 
  TableBody
} from '@/components/ui/table';
import { SmartMeterTableHeader, SmartMeterTableRow, EmptyTableRow } from './table';
import TableFilters from './TableFilters';
import { useSmartMeterFilters } from './hooks/useSmartMeterFilters';

interface SmartMeter {
  deviceId: string;
  location: string;
  status: string;
  batteryLevel: number;
  signalStrength: number;
  lastCommunication: string;
  firmwareVersion: string;
  readings: {
    voltage: number;
    current: number;
    frequency: number;
    power: number;
    totalEnergy: number;
  };
  maintenanceStatus: {
    lastMaintenance: string;
    nextScheduled: string;
    alerts: string[];
  };
}

interface SmartMeterTableProps {
  smartMeters: SmartMeter[];
  onSelectDevice: (deviceId: string) => void;
}

// Define FilterOption interface
interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

const SmartMeterTable: React.FC<SmartMeterTableProps> = ({ 
  smartMeters,
  onSelectDevice 
}) => {
  // State for active filters
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({
    Location: [],
    Status: [],
    BatteryLevel: [],
    SignalStrength: []
  });

  // Use the custom hook to get filter categories and filtered meters
  const { filterCategories, filteredMeters } = useSmartMeterFilters(smartMeters, activeFilters);

  // Helper function to convert filterCategories array to the new Record format
  const convertFilterCategoriesToRecord = (categories: { name: string; options: FilterOption[] }[]): Record<string, { label: string; value: string; count?: number }[]> => {
    const result: Record<string, { label: string; value: string; count?: number }[]> = {};
    
    categories.forEach(category => {
      result[category.name] = category.options.map(option => ({
        label: option.label,
        value: option.value,
        count: option.count
      }));
    });
    
    return result;
  };

  // Handle filter changes
  const handleFilterChange = (category: string, value: string) => {
    setActiveFilters(prev => {
      const newFilters = { ...prev };
      
      if (newFilters[category].includes(value)) {
        // Remove the filter if it's already active
        newFilters[category] = newFilters[category].filter(v => v !== value);
      } else {
        // Add the filter
        newFilters[category] = [...newFilters[category], value];
      }
      
      return newFilters;
    });
  };

  // Clear all filters
  const handleClearFilters = () => {
    setActiveFilters({
      Location: [],
      Status: [],
      BatteryLevel: [],
      SignalStrength: []
    });
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 py-2 px-2 flex-shrink-0">
        <TableFilters 
          categories={convertFilterCategoriesToRecord(filterCategories)}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />
        {Object.values(activeFilters).some(filters => filters.length > 0) && (
          <div className="text-xs sm:text-sm text-muted-foreground">
            Showing {filteredMeters.length} of {smartMeters.length} devices
          </div>
        )}
      </div>
      
      <div className="border overflow-auto flex-grow h-[calc(100vh-20rem)]">
        <Table>
          <SmartMeterTableHeader />
          <TableBody>
            {filteredMeters.map((meter) => (
              <SmartMeterTableRow 
                key={meter.deviceId}
                meter={meter}
                onSelectDevice={onSelectDevice}
              />
            ))}
            {filteredMeters.length === 0 && (
              <EmptyTableRow 
                colSpan={7} 
                message={smartMeters.length === 0 
                  ? 'No smart meter data found' 
                  : 'No devices match current filters'
                }
              />
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default SmartMeterTable;
